import { DatePipe } from '@angular/common';
import { AfterViewInit, Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DataStateChangeEventArgs, FilterSearchBeginEventArgs, FilterSettingsModel, GridComponent, RowDataBoundEventArgs, TextWrapSettingsModel } from '@syncfusion/ej2-angular-grids';
import { ClickEventArgs } from '@syncfusion/ej2-angular-navigations';
import { Observable, of, Subject, Subscription } from 'rxjs';
import { catchError, take, takeUntil, tap } from 'rxjs/operators';
import { AppSettingService } from 'src/app/services/app-setting.service';
import { ClickStreamAnalyticsService, EventType } from 'src/app/services/clickstream-analytics-service';
import { DataSharingService } from 'src/app/services/data-sharing.service';
import { PinsService } from 'src/app/services/pins.service';
import { SyncfusionService } from 'src/app/services/syncfusion.service';

@Component({
  selector: 'app-grid-reusable-molecule',
  templateUrl: './grid-reusable-molecule.component.html',
  styleUrls: ['./grid-reusable-molecule.component.scss']
})
export class GridReusableMoleculeComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild("grid", { static: false })
  public grid: GridComponent;
  @Input() freezeColumns: number = 2;
  @Input() reportName: any;
  @Input() dynamic_headers: any;
  @Input() count: any;
  @Input() data: any;
  @Input() isOptionRequired: boolean = false;
  @Input() returnsAnalyzerOptions: boolean;
  @Input() readOnlyMode: boolean = true;
  @Input() dynamicNormForm: FormGroup;
  @Output() navigateGrid = new EventEmitter<DataStateChangeEventArgs>();
  @Output() dataEmitted = new EventEmitter<string>();
  @Output() dataEmitForDynamicNormReview = new EventEmitter<{}>();
  @Output() dataEmitForPOPUP = new EventEmitter<{}>();
  @Output() dataEmitForSalesEvent = new EventEmitter<{}>();
  @Output() edit = new EventEmitter<any>();
  @Output() delete = new EventEmitter<any>();
  @Output() reloadPageEvent = new EventEmitter<void>();
  @Input() workOrderData: { [sku: string]: string } = {};
  public pageSettings: object;
  public toolbar: string[];
  public loadingIndicator;
  public editSettings;
  public wrapSettings: TextWrapSettingsModel = { wrapMode: 'Both' };
  public filterOption: FilterSettingsModel = { type: "Excel" };
  public datePipe = new DatePipe("en-US");
  private destroy$: Subject<void> = new Subject<void>();
  private triggerRestoreSubscription!: Subscription;
  cachedGridData: any;
  uniqueCodes: any[] = [];
  cachedPinCounts: any = {};
  isMobile: boolean = false;
  isLoading = false;
  templateType: string = 'text';
  columnTypes: { [key: string]: string } = {};
  // gridData: any[] = [];
  pagination: boolean = true;
  skuLineGraphFlag: boolean = false;
  customOperators = {
    stringOperator: [
      { value: 'startswith', text: 'Starts with' },
      { value: 'equal', text: 'Equal' },
      { value: 'contains', text: 'Contains' }
    ],
    numberOperator: [
      { value: 'equalto', text: 'Equal to' },
      { value: 'notequalto', text: 'Not Equal to' },
      { value: 'greaterthan', text: 'Greater than' },
      { value: 'lessthan', text: 'Less than' },
      { value: 'greaterthanorequal', text: 'Greater than Or Equal' },
      { value: 'lessthanorequal', text: 'Less than Or Equal' }
    ]
  };

  constructor(private settingService: AppSettingService, private syncfusion: SyncfusionService, private snack: MatSnackBar,
    private clickStream: ClickStreamAnalyticsService, private pinsService: PinsService, private dataSharingService: DataSharingService) { }

  ngOnInit() {
    this.modifyFilters();
    this.triggerRestoreSubscription = this.dataSharingService.triggerRestore$.subscribe(() => {
      this.restoreGridScrollPosition();
    });
    if (this.dynamic_headers && this.data) {
      this.cachedGridData = this.data;
    }

    this.checkScreenSize();
    this.editSettings = {
      allowEditing: true,
      mode: 'Normal',
    };
    this.updateToolbarBasedOnGridMode();
    if (this.dynamic_headers?.length > 0) {
      this.mapColumns();
    }
    let settings = this.settingService.getAppSetting('grid.pageSizes', [50, 100]);
    this.pageSettings = {
      pageSizes: settings,
      pageSize: settings[0],
      locale: "en-US"
    };
    this.loadingIndicator = { indicatorType: "Shimmer" };
    if (this.isOptionRequired) {
      this.pageSettings = {};
      this.toolbar = [];
      this.skuLineGraphFlag = true;
      this.pagination = false;
    }
    if (this.isOptionRequired) {
      this.pageSettings = {};
    }
    if (this.returnsAnalyzerOptions) {
      this.isOptionRequired = true;
    }
  }
  ngAfterViewInit() {
    this.dataSharingService.setGridElement(this.grid);
    this.getUniqueCodes(this.cachedGridData);
  }
  modifyFilters() {
    this.dynamic_headers = this.dynamic_headers.map(header => ({
      ...header,
      filter: header.filter ?? (header.state
        ? { type: 'CheckBox' }
        : ['openPOQty', 'canOrderQty'].includes(header.field)
          ? { type: 'Excel' }
          : undefined)
    }));

  }

  mapColumns() {
    this.dynamic_headers.forEach(header => {
      // this.columnTypes[header.field] = header.image ? 'image' : 'text';
      switch (true) {
        case header.image:
          this.columnTypes[header.field] = 'image';
          break;
        case header.percentage:
          this.columnTypes[header.field] = 'percentage';
          break;
        case header.state:
          this.columnTypes[header.field] = 'state';
          break;
        case header.pins:
          this.columnTypes[header.field] = 'pins';
          break;
        case header.navigate:
          this.columnTypes[header.field] = 'navigate';
          break;
        case header.number:
          this.columnTypes[header.field] = 'number';
          break;
        // case header.field === 'openPOQty' || header.field === 'canOrderQty':
        case ['openPOQty', 'canOrderQty'].includes(header.field):
          this.columnTypes[header.field] = 'openPO';
          break;
        case header.field === 'decision':
          this.columnTypes[header.field] = 'decision';
          break;
        case header.field == 'createWorkOrder':
          this.columnTypes[header.field] = 'createWO';
          break;
        case header.field == 'planned_delivery_date':
          this.columnTypes[header.field] = 'plannedETA';
          break;
        case header.field == 'customNormValue':
          this.columnTypes[header.field] = 'customNormValue';
          break;
        case header.field == 'analysis':
          this.columnTypes[header.field] = 'analysis';
          break;
        case header.field == 'unavailableSkuCodes':
          this.columnTypes[header.field] = 'unavailableSkuCodes';
          break;
        case header.displayFormat === 'currency':
          this.columnTypes[header.field] = 'currency';
          break;
        case header.field.includes('action'):
          this.columnTypes[header.field] = 'action';
          break;
        default:
          this.columnTypes[header.field] = 'text';
      }
    });
  }
  updateGridHandler(material: string, field: string, value: any) {
    this.grid.setCellValue(material, field, value);
  }

  showSnackbar(message: string) {
    this.snack.open(message, 'close', {
      duration: 4000,
      horizontalPosition: 'center',
      verticalPosition: 'top'
    });
  }
  updateToolbarBasedOnGridMode(): void {
    const isRemote = this.settingService.isRemoteGridMode();
    this.toolbar = isRemote ? ['ExcelExport', 'ColumnChooser'] : ['ExcelExport', 'CsvExport', 'ColumnChooser'];
  }
  restoreGridScrollPosition() {
    this.dataSharingService.currentPosition$.pipe(take(1)).subscribe(position => {
      setTimeout(() => {
        if (this.grid && this.grid.element) {
          let movableContent = this.grid.element.querySelector('.e-movablecontent') as HTMLElement;
          if (movableContent) {
            movableContent.scrollTo({
              left: position,
              behavior: 'smooth'
            });
          }
        }
      }, 500);
    });
  }

  getColumnType(field: any) {
    const header = this.dynamic_headers.find((h) => h.field === field);
    if (header.image === true) {
      this.templateType = 'image';

      return this.templateType;
    } else {
      this.templateType = 'text';

      return this.templateType;
    }
  }
  @HostListener('window:resize', [])
  onResize() {
    this.checkScreenSize();
  }
  checkScreenSize() {
    const screenWidth = window.innerWidth;
    this.isMobile = screenWidth < 768;
    this.freezeColumns = this.isMobile ? 1 : 2;
    if (this.reportName == "promotions-planner") {
      this.freezeColumns = 2;
    }
  }
  actionBegin(args: FilterSearchBeginEventArgs) {
    if (args.requestType != 'filterchoicerequest') {
      this.clickStream.publishGAEvent(EventType.GRID_COLUMN_ACTION, {
        actionType: args.requestType,
        value: args.columnName,
        section: this.reportName
      })
    }
    if (
      args.requestType === "filterchoicerequest" ||
      args.requestType === "filtersearchbegin"
    ) {
      args.filterChoiceCount = this.count;
    }
    if (args.requestType === "filterbeforeopen") {
      setTimeout(() => {
        const filterDialog = (args as any).filterModel?.dlgDiv;
        if (filterDialog) {
          const filterElement = filterDialog.querySelector('.e-autocomplete');
          if (filterElement && filterElement.ej2_instances) {
            filterElement.ej2_instances[0].actionBegin = (args) => {
              args.cancel = true;
            }
          };
        }
      }, 0);
      if (this.settingService.isRemoteGridMode()) {
        if (args.filterModel['flMuiObj'] && args.filterModel['flMuiObj'].customFilterOperators) {
          args.filterModel['flMuiObj'].customFilterOperators.stringOperator = this.customOperators.stringOperator;
          args.filterModel['flMuiObj'].customFilterOperators.numberOperator = this.customOperators.numberOperator;
        }
        if (args.filterModel['cBox']) {
          const div = args.filterModel['cBox'];
          div.style.display = 'none';
        }
      }

    }
  }
  public triggerExcelExportFromOutside(): void {
    this.toolbarClick({ item: { text: 'Excel Export' } } as any);
  }
  toolbarClick(args: ClickEventArgs): void {
    this.clickStream.publishGAEvent(EventType.GRID_COLUMN_ACTION, {
      actionType: args.item.text,
      section: this.reportName
    })
    switch (args.item.text) {
      case "Excel Export":
        // this.grid.excelExport(this.getExcelExportProperties());
        if (this.settingService.isRemoteGridMode()) {
          this.triggerExcelExportApi();
        }
        else {
          this.grid.excelExport(this.getExcelExportProperties());
        }
        break;
      case "CSV Export":
        this.grid.csvExport(this.getCSVExportProperties());
        break;
    }
  }

  private getExcelExportProperties(): any {
    let currentDateTime = this.datePipe.transform(
      new Date(),
      "MM_dd_yy_hh:mm:ss"
    );
    return {
      fileName: this.reportName + "_" + currentDateTime + ".xlsx",
    };
  }
  private getCSVExportProperties(): any {
    let currentDateTime = this.datePipe.transform(
      new Date(),
      "MM_dd_yy_hh:mm:ss"
    );
    return {
      fileName: this.reportName + "_" + currentDateTime + ".csv",
    };
  }

  private triggerExcelExportApi(): void {
    this.syncfusion?.exportData$?.pipe(takeUntil(this.destroy$)).subscribe((exportData) => {
      this.isLoading = true;
      if (exportData !== null && exportData?.headerContent && exportData?.pageData) {
        const headerContent = JSON.parse(exportData.headerContent);
        const columnsMapping = headerContent.reduce((acc: { [key: string]: string }, item: any) => {
          if (item.field && item.headerText) {
            acc[item.field] = item.headerText;
          }
          return acc;
        }, {});

        const data = {
          ...exportData.pageData,
          columns: columnsMapping,
          downloadEntity: false
        };
        this.syncfusion.exportDataToExcel(data, this.reportName).subscribe((response) => {
          if (response.status === 307) {
            this.grid.excelExport(this.getExcelExportProperties()).then(() => {
              setTimeout(() => {
                this.isLoading = false;
              }, 1000);
            }).catch(() => {
              this.isLoading = false;
              console.error('Error exporting to Excel');
            });
          } else {
            // Handle successful response
            this.isLoading = false;
            this.showSnackbar('Download triggered Successfully');
          }
        }, (error) => {
          this.isLoading = false;
          console.error('Error in Excel Export:', error);
        });
      } else {
        this.isLoading = true;
        this.grid.excelExport(this.getExcelExportProperties()).then(() => {
          setTimeout(() => {
            this.isLoading = false;
          }, 1000);
        }).catch(() => {
          this.isLoading = false;
          console.error('Error exporting to Excel');
        });
      }
    });
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.syncfusion.resetExportData();
    if (this.triggerRestoreSubscription) {
      this.triggerRestoreSubscription.unsubscribe();
    }
  }
  onRowSelected(event) {
    this.dataEmitted.emit(event);
  }
  public rowDataBound(args: RowDataBoundEventArgs) {
    args.rowHeight = 48;
  }
  public dataStateChange(state: DataStateChangeEventArgs): void {
    this.navigateGrid.emit(state);
  }
  handlePOPUP(event: { header: any; fieldData: any }) {
    this.dataEmitForPOPUP.emit(event);
  }
  handleSalesEventData(event: any) {
    this.dataEmitForSalesEvent.emit(event);
  }
  editData(event:any){
    this.edit.emit(event);
  }
  deleteData(event:any){
    this.delete.emit(event);
  }
  getUniqueCodes(data: any[]): void {
    const codes = new Set();
    // this.uniqueCodes = Array.from(codes);
    // this.fetchPinCounts(this.uniqueCodes).subscribe(pinCounts => {
    //   this.cachedPinCounts = pinCounts;
    //   console.log(this.cachedPinCounts, "Fetched pin counts");
    // });
    const gridData: any = data && data['result'] ? data['result'] : data;
    gridData?.forEach((item: any) => {
      this.dynamic_headers?.forEach((header: any) => {
        if (header.pins && item[header.field] !== undefined) {
          codes.add(item[header.field]);
        }
      });
    });
    this.uniqueCodes = Array.from(codes);
    this.fetchPinCounts(this.uniqueCodes).subscribe(pinCounts => {
      this.cachedPinCounts = pinCounts;
    });
  }

  fetchPinCounts(codes: string[]): Observable<any> {
    return this.pinsService.getPinsCount(codes).pipe(
      tap(pinCounts => {
      }),
      catchError(error => {
        console.error('Error fetching pin counts:', error);
        return of({});
      })
    );
  }
  refreshPinCount() {
    this.fetchPinCounts(this.uniqueCodes).subscribe(pinCounts => {
      this.cachedPinCounts = pinCounts;
    });
  }
}
